/**
 * v2 API error types.
 *
 * Mirrors the backend error envelope from `app/api/v2/errors.py`.
 * All v2 client calls normalize HTTP errors into this shape.
 */

export interface ApiV2ErrorBody {
  error: {
    code: string;
    message: string;
    request_id?: string | null;
    details?: Array<{ field?: string; message?: string }> | null;
  };
}

/**
 * Typed error class for v2 API responses.
 *
 * Usage in catch blocks:
 * ```ts
 * try { ... }
 * catch (err) {
 *   if (err instanceof ApiV2Error) {
 *     console.log(err.code, err.requestId);
 *   }
 * }
 * ```
 */
export class ApiV2Error extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly requestId: string | null;
  public readonly details: Array<{ field?: string; message?: string }> | null;

  constructor(status: number, body: ApiV2ErrorBody) {
    super(body.error.message);
    this.name = "ApiV2Error";
    this.status = status;
    this.code = body.error.code;
    this.requestId = body.error.request_id ?? null;
    this.details = body.error.details ?? null;
  }
}

/**
 * Parse an HTTP Response into an ApiV2Error.
 * Returns null if the response body doesn't match the v2 error envelope.
 */
export async function parseV2Error(res: Response): Promise<ApiV2Error | null> {
  try {
    const body = (await res.json()) as ApiV2ErrorBody & { detail?: unknown };
    if (body?.error?.code && body?.error?.message) {
      return new ApiV2Error(res.status, body);
    }
    // Services core proxies verbatim (call analytics) answer with FastAPI's {"detail": ...};
    // without this their validation messages collapse into "422 Unprocessable Entity".
    if (typeof body?.detail === "string" && body.detail) {
      return new ApiV2Error(res.status, { error: { code: "unknown_error", message: body.detail } });
    }
    if (Array.isArray(body?.detail) && body.detail.length) {
      const details = body.detail.map((item: { loc?: unknown[]; msg?: string }) => ({
        field: Array.isArray(item?.loc) ? item.loc.filter((part) => part !== "body").join(".") : undefined,
        message: item?.msg ?? JSON.stringify(item),
      }));
      const message = details.map((d) => (d.field ? `${d.field}: ${d.message}` : d.message)).join("; ");
      return new ApiV2Error(res.status, { error: { code: "validation_error", message, details } });
    }
  } catch {
    // Response body wasn't JSON or didn't match the envelope.
  }
  return null;
}

/**
 * The envelope from an axios rejection. Feature code still on raw axios reads v2 errors
 * through this rather than `response.data.detail`, which v2 never sends.
 */
export function readAxiosV2Error(
  error: unknown,
): { status: number | null; code: string | null; message: string | null } {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const body = response?.data as Partial<ApiV2ErrorBody> & { detail?: unknown } | undefined;
  const code = typeof body?.error?.code === "string" ? body.error.code : null;
  const message = typeof body?.error?.message === "string"
    ? body.error.message
    : typeof body?.detail === "string" ? body.detail : null;
  return { status: typeof response?.status === "number" ? response.status : null, code, message };
}
